import { supabase } from './supabase'

// ── inviteShare — one source of truth for the "invite a friend" action ────────
// The personal invite link + personalized message logic lived only inside
// InviteTab (Profile > Invite, three taps deep). Extracted here so any surface
// can fire a one-tap invite without duplicating the message copy or the
// column-scoped code lookup.

// Resolve the current user's personal invite URL. personal_invite_code is
// column-scoped out of direct profile SELECTs for authenticated users, so it
// must come from the caller's already-loaded profile or the definer RPC.
export async function getInviteUrl(profile) {
  const code = profile?.personal_invite_code
  if (code) return `${window.location.origin}/invite/${code}`
  try {
    const { data } = await supabase.rpc('get_my_profile').maybeSingle()
    if (data?.personal_invite_code) return `${window.location.origin}/invite/${data.personal_invite_code}`
  } catch (_) {}
  return null
}

// Build a personalized invite message from what the user is actually watching.
// Mirrors the tiers InviteTab has always used so the copy never diverges.
export function inviteMessage(profile, wl, url) {
  const watching = (wl?.items || [])
    .filter(s => wl?.statuses?.[s.id] === 'watching' && s.name)
    .slice(0, 3)
    .map(s => s.name)
  const completed = (wl?.items || []).filter(s => wl?.statuses?.[s.id] === 'completed').length

  if (watching.length >= 2) {
    return `Hey! I'm using NextEp to track ${watching.slice(0, -1).join(', ')} and ${watching[watching.length - 1]}. You should join, it's the best TV tracker out there. ${url}`
  }
  if (completed >= 10) {
    return `I've tracked ${completed} completed series on NextEp. Join me and track everything you watch too! ${url}`
  }
  if (watching.length === 1) {
    return `I'm using NextEp to track ${watching[0]} and everything else I watch. You should try it! ${url}`
  }
  return `Join me on NextEp, the best TV tracker around. ${url}`
}

// One-tap invite: resolve the link, open the native share sheet, and fall back
// to copying the link where Web Share isn't available (desktop). Returns one of
// 'shared' | 'copied' | 'cancelled' | 'failed' so callers can toast accordingly.
export async function shareInvite(profile, wl) {
  const url = await getInviteUrl(profile)
  if (!url) return 'failed'
  const text = inviteMessage(profile, wl, url)
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Join NextEp', text, url })
      return 'shared'
    } catch (_) {
      return 'cancelled' // user dismissed the sheet, don't also copy
    }
  }
  try {
    await navigator.clipboard?.writeText(url)
    return 'copied'
  } catch (_) {
    return 'failed'
  }
}
